/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `index` for the Caintra Connect frontend.
 * @module components/BusinessEventAttendancesTable/index
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import Switch from "@mui/material/Switch";
import styles from "./index.module.css";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

const BusinessEventAttendancesTable = ({
  attendances,
  // sortConfig,
  onSort,
  handleToggleActive,
  renderArrow,
}) => {
  const { t } = useTranslation();

  const formatSchedule = (attendance) => {
    const appointmentDate = dayjs(attendance.fechaEvento)
      .utc()
      .format("DD/MM/YYYY");
    return `${appointmentDate} ${attendance.horaInicio} - ${attendance.horaFin}`;
  };

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th className={styles.fullNameColumn} onClick={onSort}>
              <div className={styles.sortableHeader}>
                {t("pages.attendance.fullName")} {renderArrow()}
              </div>
            </th>
            <th className={styles.emailColumn}>
              {t("pages.attendance.email")}
            </th>
            {/* <th className={styles.comercialNameColumn}>
              {t("pages.attendance.commercialName")}
            </th> */}
            <th className={styles.memberNumberColumn}>
              {t("pages.attendance.memberNumber")}
            </th>
            <th className={styles.rfcColumn}>{t("pages.attendance.rfc")}</th>
            <th className={styles.eventNameColumn}>
              {t("pages.attendance.eventName")}
            </th>
            <th className={styles.buyerCompanyNameColumn}>
              {t("pages.attendance.buyerCompanyName")}
            </th>
            <th className={styles.requirementNameColumn}>
              {t("pages.attendance.requirementName")}
            </th>
            <th className={styles.scheduleColumn}>
              {t("pages.attendance.schedule")}
            </th>
            <th className={styles.attendanceColumn}>
              {t("pages.attendance.attended")}
            </th>
          </tr>
        </thead>
        <tbody>
          {attendances.map((attendance, index) => (
            <tr
              key={index}
              className={!attendance.asistio ? styles.inactiveRow : ""}
            >
              <td className={styles.userNameContainer}>
                <span className={styles.userName}>
                  {attendance.nombreUsuario}
                </span>
                <span className={styles.companyName}>
                  {attendance.nombreEmpresa}
                </span>
              </td>
              <td>
                {attendance.correoElectronicoUsuario
                  ? attendance.correoElectronicoUsuario
                  : "-"}
              </td>
              <td>
                {attendance.numeroSocioEmpresa
                  ? attendance.numeroSocioEmpresa
                  : "-"}
              </td>
              <td>{attendance.rfcEmpresa ? attendance.rfcEmpresa : "-"}</td>
              <td>
                {attendance.nombreEncuentroNegocios
                  ? attendance.nombreEncuentroNegocios
                  : "-"}
              </td>
              <td>
                {attendance.nombreEmpresaCompradora
                  ? attendance.nombreEmpresaCompradora
                  : "-"}
              </td>
              <td>
                {attendance.nombreRequerimiento
                  ? attendance.nombreRequerimiento
                  : "-"}
              </td>
              <td>{formatSchedule(attendance)}</td>
              <td>
                <Switch
                  checked={attendance.asistio}
                  onChange={() => handleToggleActive(index)}
                  color="primary"
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default BusinessEventAttendancesTable;
